import {
  ColDef,
  type GridApi,
  GridReadyEvent,
  type IRowNode,
  type ModelUpdatedEvent,
  ModuleRegistry,
} from 'ag-grid-community'
import { AllEnterpriseModule } from 'ag-grid-enterprise'
import {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Control } from '@/gen/inventory/v1/control_service_pb'

import { type AgGridSelectionEventType } from '@/lib/ag-grid/ag-grid-selection-changed.hook'
import {
  mapAgGridRowsToIds,
  useAgGridSelectionChanged,
} from '@/lib/ag-grid/ag-grid-selection-changed.hook'
import { useHasPermissions } from '@/lib/app/check-permissions.hook'
import { cn } from '@/lib/ui/style-helpers'

import { CheckboxWithLabel } from '@/components/lemmala-design-system/checkbox/checkbox'
import { AgDataTable } from '@/components/lemmala-design-system/table'
import type { AgDataTableTrackingContext } from '@/components/lemmala-design-system/table'
import { TABLE_ROW_HEIGHTS } from '@/components/lemmala-design-system/table/consts'
import { useFilterModelEvaluator } from '@/components/lemmala-design-system/table/hooks/use-filter-model-evaluator'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

import { getControlColumnDefs } from './config/column-defs'
import { type ViewName, getViewDefinition, isValidViewName, viewOptions } from './config/views'
import { type GroupingStrategy, SCOPE_GROUPING } from './grouping-strategies'
import { AG_CONTROL_TABLE_LABELS } from './labels'
import type { ControlRow } from './types'
import { filterAndSortActiveControls } from './utils/filter-and-sort-active-controls'

// Register AG Grid modules
ModuleRegistry.registerModules([AllEnterpriseModule])

/** BigInt-safe stringify check – mirrors the safeStringify fallback in AgDataTable */
const matchesByStringify = (data: ControlRow, query: string): boolean => {
  const str = JSON.stringify(data, (_k, v) => (typeof v === 'bigint' ? v.toString() : v)) || ''
  return str.toLowerCase().includes(query.toLowerCase())
}

export type AgControlTableRef = {
  clearSelection: () => void
}

export type AgControlTableProps = {
  controls: Control[]
  isLoading?: boolean
  readOnly?: boolean
  hideEvidenceRequest?: boolean
  disableViewSelection?: boolean
  tableHeaderActions?: ReactNode
  onRowClick?: (control: Control) => void
  filterContext?: string
  // Disables the group by checkbox and hides the group by icon in the header. MUST be used when selection is enabled.
  disableGroupBy?: boolean
  groupByDefault?: boolean
  selection?: {
    enabled: boolean
    multiple?: boolean
    selectOnClick?: boolean
    initialSelectedIds?: string[]
    onSelectionChanged?: (ids: string[]) => void
    onSelectionEvent?: (type: AgGridSelectionEventType, count: number) => void
  }
  // Important to allow filter popovers to catch interactions
  inModal?: boolean
  // Optional custom column definitions. If provided, overrides default column generation.
  columnDefs?: ColDef<ControlRow>[]
  rowHeight?: keyof typeof TABLE_ROW_HEIGHTS
  // When true (default), uses dynamic height calculation. When false, uses autoHeight for fit-content.
  dynamicHeight?: boolean
  // Callback when groupBy state changes
  onGroupByChange?: (groupBy: boolean) => void
  hideSearch?: boolean
  hideCount?: boolean
  searchPlacement?: 'left' | 'right'
  // Callback reporting visible non-group-header control IDs in display order
  onVisibleRowOrderChange?: (ids: string[]) => void
  groupingStrategy?: GroupingStrategy
  containerClassName?: string
  toolbarClassName?: string
  className?: string
  // Analytics `from` value for status changes fired from this table's status
  // cells. Omit for read-only contexts where the modal cannot open.
  from?: string
  // Optional per-row class name, composed with the built-in group-header rows.
  getRowClassName?: (data?: ControlRow) => string
  trackingContext?: AgDataTableTrackingContext
  onViewChange?: (viewName: ViewName) => void
  onSearchChange?: (query: string) => void
  onGroupHeaderClick?: (scopeName: string) => void
  defaultVisibleFilters?: string[]
}

export const AgControlTable = forwardRef<AgControlTableRef, AgControlTableProps>(
  function AgControlTable(
    {
      controls,
      isLoading = false,
      readOnly = false,
      hideEvidenceRequest = true,
      tableHeaderActions,
      disableViewSelection = false,
      onRowClick,
      filterContext = 'control',
      disableGroupBy,
      groupByDefault = true,
      selection,
      inModal,
      columnDefs: customColumnDefs,
      rowHeight = 'single',
      dynamicHeight = true,
      onGroupByChange,
      hideSearch = false,
      hideCount = false,
      searchPlacement = 'right',
      toolbarClassName,
      onVisibleRowOrderChange,
      groupingStrategy = SCOPE_GROUPING,
      containerClassName,
      className,
      from,
      trackingContext,
      onViewChange,
      onSearchChange,
      onGroupHeaderClick,
      defaultVisibleFilters = ['status', 'scope'],
      getRowClassName,
    }: AgControlTableProps,
    ref: React.Ref<AgControlTableRef>,
  ) {
    const hasManagePermissions = useHasPermissions(['base.manage'])

    const gridApiRef = useRef<GridApi<ControlRow> | null>(null)

    useImperativeHandle(ref, () => ({
      clearSelection: () => gridApiRef.current?.deselectAll(),
    }))
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

    const resolvedContainerClassName = dynamicHeight
      ? (containerClassName ?? 'h-[560px]')
      : undefined
    // Track group by toggle state - initialize from URL
    const [groupBy, setGroupBy] = useState(!disableGroupBy && groupByDefault)

    const handleGroupByChange = useCallback(
      (checked: boolean) => {
        setGroupBy(checked)
        onGroupByChange?.(checked)
      },
      [onGroupByChange],
    )

    // Reset grid sort state when groupBy changes
    useEffect(() => {
      const api = gridApiRef.current
      if (api) {
        const colState = api
          .getColumnState()
          .map((col) => ({ ...col, sort: null, sortIndex: null }))
        api.applyColumnState({ state: colState })
      }
    }, [groupBy])

    // Track current view from AgDataTable
    const [currentViewName, setCurrentViewName] = useState<ViewName>('All')

    // Toggle scope group expansion
    const toggleGroup = useCallback((scopeName: string) => {
      setExpandedGroups((prev) => {
        const next = new Set(prev)
        if (next.has(scopeName)) {
          next.delete(scopeName)
        } else {
          next.add(scopeName)
        }
        return next
      })
    }, [])

    // Filter to only active controls and sort by title
    const activeControls = useMemo(() => filterAndSortActiveControls(controls), [controls])

    // Check if all controls have weight 0 - hide weight column if so
    const allWeightsAreZero = useMemo(
      () => activeControls.every((control) => !control.controlWeight),
      [activeControls],
    )

    // Get column definitions - use custom if provided, otherwise generate default
    const columnDefs = useMemo(
      () =>
        customColumnDefs ||
        getControlColumnDefs(
          activeControls,
          readOnly,
          hasManagePermissions,
          hideEvidenceRequest,
          toggleGroup,
          groupBy,
          allWeightsAreZero,
          from,
        ),
      [
        customColumnDefs,
        activeControls,
        readOnly,
        hasManagePermissions,
        hideEvidenceRequest,
        toggleGroup,
        groupBy,
        allWeightsAreZero,
        from,
      ],
    )

    // Refresh headers when column defs change so that updated headerComponentParams
    // (e.g. innerHeaderComponent) are picked up by AG Grid.
    useEffect(() => {
      gridApiRef.current?.refreshHeader()
    }, [columnDefs])

    // Create filter evaluator for view counters
    const evaluate = useFilterModelEvaluator<ControlRow>(columnDefs)

    // Calculate view counters from all active controls (before grouping/filtering)
    // This ensures counts remain consistent regardless of grouping state
    const viewCounters = useMemo(() => {
      const result: Record<string, number> = {}
      for (const viewName of viewOptions) {
        const viewDef = getViewDefinition(viewName)
        const count = activeControls.reduce(
          (acc, control) =>
            evaluate(control as ControlRow, viewDef.filterModel || {}) ? acc + 1 : acc,
          0,
        )
        result[viewName] = count
      }
      return result
    }, [activeControls, evaluate])

    // Process controls with optional grouping
    const processedData = useMemo<ControlRow[]>(() => {
      if (!groupBy) {
        return activeControls
      }

      const currentViewDef = getViewDefinition(currentViewName)
      const filteredControls = currentViewDef.filterModel
        ? activeControls.filter((control) =>
            evaluate(control as ControlRow, currentViewDef.filterModel || {}),
          )
        : activeControls

      return groupingStrategy.groupFn(filteredControls, expandedGroups)
    }, [groupBy, currentViewName, activeControls, expandedGroups, evaluate, groupingStrategy])

    const [areRowsSelected, setAreRowsSelected] = useState(false)
    const handleSelectedCountChange = useCallback((count: number) => {
      setAreRowsSelected(count > 0)
    }, [])
    const onSelectionChanged = useAgGridSelectionChanged({
      selection,
      getSelectedIds: mapAgGridRowsToIds,
      onSelectedCountChange: handleSelectedCountChange,
    })

    const handleGridReady = useCallback(
      (e: GridReadyEvent<ControlRow>) => {
        gridApiRef.current = e.api
        if (selection?.enabled && selection.initialSelectedIds) {
          const selectedNodes: IRowNode<Control>[] = []
          for (const id of selection.initialSelectedIds) {
            const node = e.api.getRowNode(id)
            if (node) {
              selectedNodes.push(node)
            }
          }
          e.api.setNodesSelected({
            nodes: selectedNodes,
            newValue: true,
          })
        }
      },
      [selection],
    )

    // Grid options
    const gridOptions = useMemo(
      () => ({
        onGridReady: handleGridReady,
        rowHeight: TABLE_ROW_HEIGHTS[rowHeight],
        suppressRowTransform: true,
        colResizeDefault: 'shift' as const,
        onSelectionChanged,
        onModelUpdated: (params: ModelUpdatedEvent<ControlRow>) => {
          if (onVisibleRowOrderChange && params) {
            const ids: string[] = []
            params.api.forEachNodeAfterFilterAndSort((node) => {
              if (node.data && !node.data.isGroupHeader) {
                ids.push(node.data.id)
              }
            })
            onVisibleRowOrderChange(ids)
          }
        },
        getRowClass: (params: { data?: ControlRow }) => {
          const customClass = getRowClassName?.(params.data) ?? ''
          if (params.data?.isGroupHeader) {
            return cn('ag-row-control-category-header', customClass)
          }
          if (params.data?.isGroupedControl) {
            return cn('ag-row-grouped-control', customClass)
          }
          return customClass
        },
      }),
      [handleGridReady, onVisibleRowOrderChange, onSelectionChanged, rowHeight, getRowClassName],
    )

    const getDefinition = useCallback(
      (name: string) => getViewDefinition(name, groupBy, allWeightsAreZero),
      [groupBy, allWeightsAreZero],
    )
    const views = useMemo(() => {
      return {
        options: viewOptions,
        getDefinition,
        defaultView: 'All' as ViewName,
        // Pass pre-calculated counters to keep counts consistent when grouping
        defaultCounters: viewCounters,
        // Include group headers in count for controls table
      }
    }, [viewCounters, getDefinition])

    const handleViewChange = useCallback(
      (name: string) => {
        // Type-safe: only set if the name is a valid view
        if (isValidViewName(name)) {
          setCurrentViewName(name)
          onViewChange?.(name)
          // Height will be recalculated when processedData updates (via view change)
        }
      },
      [onViewChange],
    )

    if (isLoading) {
      return (
        <div className='flex h-64 items-center justify-center'>
          <div className='text-main-foreground-tertiary'>{AG_CONTROL_TABLE_LABELS.LOADING}</div>
        </div>
      )
    }

    return (
      <AgDataTable<ControlRow>
        domLayout={dynamicHeight ? 'normal' : 'autoHeight'}
        className={cn(dynamicHeight ? undefined : 'min-h-36', className)}
        rowData={processedData}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        onGridReady={(api) => {
          gridApiRef.current = api
        }}
        views={disableViewSelection ? undefined : views}
        onViewChange={handleViewChange}
        search={{}}
        onSearchChange={onSearchChange}
        inModal={inModal}
        urlSync={{
          enabled: true,
          prefix: filterContext,
          serializers: {
            sortModelToUrlParam: () => null,
            urlParamToSortModel: () => [],
          },
        }}
        hideExport
        trackingContext={trackingContext}
        onRowClick={(row: ControlRow) => {
          if (row.isGroupHeader) {
            onGroupHeaderClick?.(row.categoryName)
            toggleGroup(row.categoryName)
            return
          }
          onRowClick?.(row as Control)
        }}
        customFilterBarLeft={
          disableGroupBy ? null : (
            <Tooltip
              asChild
              // We don't support group by with selection
              tooltipDisabled={!areRowsSelected}
              trigger={
                <span>
                  <CheckboxWithLabel
                    checked={groupBy}
                    onCheckedChange={(checked) => handleGroupByChange(!!checked)}
                    disabled={areRowsSelected}
                  >
                    {groupingStrategy.checkboxLabel}
                  </CheckboxWithLabel>
                </span>
              }
            >
              {AG_CONTROL_TABLE_LABELS.CLEAR_SELECTION_TO_GROUP}
            </Tooltip>
          )
        }
        filters={{
          defaultVisibleFilters,
        }}
        headerRight={tableHeaderActions}
        selection={
          // We don't support group by with selection
          groupBy
            ? undefined
            : {
                mode: selection?.multiple ? 'multiple' : 'singleRow',
                checkboxes: selection?.enabled ?? false,
                selectOnClick: selection?.selectOnClick ?? false,
              }
        }
        entityName='control'
        containerClassName={resolvedContainerClassName}
        hideSearch={hideSearch}
        hideCount={hideCount}
        searchPlacement={searchPlacement}
        toolbarClassName={toolbarClassName}
        grouping={
          groupingStrategy.enableGroupCounting
            ? {
                isGroupHeader: (row) => !!row.isGroupHeader,
                isExpanded: (row) => !!row.isExpanded,
                getChildren: (row) => (row.categoryChildren ?? []) as ControlRow[],
                doesChildMatchSearch: matchesByStringify,
                doesChildMatchFilter: (child, filterModel) => evaluate(child, filterModel),
              }
            : undefined
        }
        columnCustomization={{
          enabled: false,
        }}
      />
    )
  },
)
